import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { getSentSkiTransfers, cancelSkiTransfer } from '../api';

export default function SentSkiTransfers({ onTransferCancelled }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [processing, setProcessing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadTransfers();
  }, []);

  const loadTransfers = async () => {
    try {
      const data = await getSentSkiTransfers();
      setTransfers(data);
    } catch (err) {
      console.error('Error loading sent transfers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (transfer) => {
    setSelectedTransfer(transfer);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedTransfer) return;
    setProcessing(true);
    try {
      console.log('Cancelling transfer:', selectedTransfer.id, selectedTransfer);
      await cancelSkiTransfer(selectedTransfer.id);
      await loadTransfers();
      setCancelDialogOpen(false);
      setSelectedTransfer(null);
      if (onTransferCancelled) {
        onTransferCancelled();
      }
    } catch (err) {
      console.error('Error cancelling transfer:', err);
      console.error('Transfer ID:', selectedTransfer.id);
      console.error('Transfer object:', selectedTransfer);
      alert(err.message || 'Failed to cancel transfer');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Accepted';
      case 'rejected': return 'Rejected';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (transfers.length === 0) {
    return null;
  }

  const pendingTransfers = transfers.filter(t => t.status === 'pending');

  return (
    <>
      {pendingTransfers.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              You have {pendingTransfers.length} pending transfer{pendingTransfers.length > 1 ? 's' : ''} you sent
            </Typography>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {pendingTransfers.map((transfer) => (
                  <Paper key={transfer.id} sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {transfer.brand} Ski #{transfer.ski_number}
                        </Typography>
                        <Chip 
                          label={getStatusLabel(transfer.status)} 
                          color={getStatusColor(transfer.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        To: {transfer.to_user_name || transfer.to_user_email}
                      </Typography>
                      {transfer.status === 'pending' && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancelClick(transfer)}
                          fullWidth
                        >
                          Cancel Transfer
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ski</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Includes</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Sent</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingTransfers.map((transfer) => (
                      <TableRow key={transfer.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {transfer.brand} Ski #{transfer.ski_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {transfer.to_user_name || transfer.to_user_email}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            {transfer.include_tests && (
                              <Chip label="Tests" size="small" variant="outlined" />
                            )}
                            {transfer.include_races && (
                              <Chip label="Races" size="small" variant="outlined" />
                            )}
                            {!transfer.include_tests && !transfer.include_races && (
                              <Typography variant="body2" color="text.secondary">None</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(transfer.status)} 
                            color={getStatusColor(transfer.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(transfer.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          {transfer.status === 'pending' && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelClick(transfer)}
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Alert>
        </Box>
      )}

      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => !processing && setCancelDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Cancel Transfer</DialogTitle>
        <DialogContent>
          {selectedTransfer && (
            <Box sx={{ mt: 1 }}>
              <Typography>
                Are you sure you want to cancel the transfer of <strong>{selectedTransfer.brand} Ski #{selectedTransfer.ski_number}</strong> to {selectedTransfer.to_user_name || selectedTransfer.to_user_email}?
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This action cannot be undone. The recipient will no longer be able to accept this transfer.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={processing}
          >
            No, Keep It
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {processing ? 'Cancelling...' : 'Yes, Cancel Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

